import { useState, useMemo } from "react";
import axios from "axios";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Building2, KeyRound } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ResetPasswordPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const token = useMemo(() => new URLSearchParams(location.search).get("token") || "", [location.search]);
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!token) return toast.error("Missing reset token");
    if (pw.length < 6) return toast.error("Password must be at least 6 characters");
    if (pw !== confirm) return toast.error("Passwords don't match");
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/api/auth/reset-password`,
        { token, new_password: pw },
        { withCredentials: true }
      );
      toast.success("Password updated. Signing you in…");
      if (refresh) await refresh();
      const role = data?.user?.role;
      const to = role === "admin" ? "/admin/dashboard" : role === "vendor" ? "/vendor/tickets" : "/resident/dashboard";
      navigate(to);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Reset failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center p-6" data-testid="reset-page">
      <Card className="w-full max-w-md bg-[#0a0a0b] border-white/10 text-white p-8">
        <div className="flex items-center gap-3 mb-6">
          <img src="/prestigious-logo.png" alt="Prestigious" className="h-12 w-12" />
        </div>

        <div className="mb-6">
          <div className="overline mb-2">Choose new password</div>
          <h2 className="text-2xl font-semibold font-display">Set a new password</h2>
          <p className="text-sm text-zinc-400 mt-1">Use at least 6 characters. You&apos;ll be signed in automatically.</p>
        </div>

        {!token && (
          <div className="text-sm text-red-300 bg-red-900/20 border border-red-500/20 rounded-md p-3 mb-4" data-testid="reset-missing-token">
            This link is missing a reset token. <Link to="/forgot-password" className="underline">Request a new one.</Link>
          </div>
        )}

        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-400">New password</Label>
            <Input
              data-testid="reset-password"
              type="password"
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              required
              minLength={6}
              className="mt-1 bg-white/[0.04] border-white/10 text-white"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-400">Confirm password</Label>
            <Input
              data-testid="reset-confirm"
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              minLength={6}
              className="mt-1 bg-white/[0.04] border-white/10 text-white"
            />
          </div>
          <Button
            data-testid="reset-submit"
            type="submit"
            disabled={loading || !token}
            className="w-full bg-orange-500 hover:bg-orange-600 text-white font-medium rounded-full h-11"
          >
            {loading ? "Updating…" : "Update password"}
          </Button>
        </form>

        <div className="mt-6 text-center text-sm text-zinc-400">
          <Link to="/login" className="text-orange-300 hover:text-orange-200 underline" data-testid="reset-back-login">Back to sign in</Link>
        </div>
      </Card>
    </div>
  );
}
